import { yupResolver } from '@hookform/resolvers/yup';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import Loader from '@/components/ui/Loader';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { Form2290Taxes } from '@/types/excise/Form2290Taxes';
import type { FrbHolidayType } from '@/types/excise/FrbHoliday';
import type { TaxCalculatorType } from '@/types/excise/TaxCalculatorType';
import { getUsDollarFormattedAmount } from '@/utils/utils';

import form2290taxes from '../../data/form2290taxes.json';
import frbholidays from '../../data/frbholidays.json';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const TaxCalculator = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isLogging, setIsLogging] = useState(false);
  const [isShowCalculateButton, setShowCalculateButton] = useState(true);
  const [dueDate, setDueDate] = useState<Date>();

  const taxYear = process.env.NEXT_PUBLIC_TAX_YEAR
    ? parseInt(process.env.NEXT_PUBLIC_TAX_YEAR, 10)
    : 0;

  const taxPeriods = [
    { value: `7-${taxYear}`, text: `July - ${taxYear}` },
    { value: `8-${taxYear}`, text: `August - ${taxYear}` },
    { value: `9-${taxYear}`, text: `September - ${taxYear}` },
    { value: `10-${taxYear}`, text: `October - ${taxYear}` },
    { value: `11-${taxYear}`, text: `November - ${taxYear}` },
    { value: `12-${taxYear}`, text: `December - ${taxYear}` },
    { value: `1-${taxYear + 1}`, text: `January - ${taxYear + 1}` },
    { value: `2-${taxYear + 1}`, text: `February - ${taxYear + 1}` },
    { value: `3-${taxYear + 1}`, text: `March - ${taxYear + 1}` },
    { value: `4-${taxYear + 1}`, text: `April - ${taxYear + 1}` },
    { value: `5-${taxYear + 1}`, text: `May - ${taxYear + 1}` },
    { value: `6-${taxYear + 1}`, text: `June - ${taxYear + 1}` },
  ];

  const weightcategories = [
    { value: 1, text: 'A (55,000 lbs)' },
    { value: 2, text: 'B (55,001 - 56,000 lbs)' },
    { value: 3, text: 'C (56,001 - 57,000 lbs)' },
    { value: 4, text: 'D (57,001 - 58,000 lbs)' },
    { value: 5, text: 'E (58,001 - 59,000 lbs)' },
    { value: 6, text: 'F (59,001 - 60,000 lbs)' },
    { value: 7, text: 'G (60,001 - 61,000 lbs)' },
    { value: 8, text: 'H (61,001 - 62,000 lbs)' },
    { value: 9, text: 'I (62,001 - 63,000 lbs)' },
    { value: 10, text: 'J (63,001 - 64,000 lbs)' },
    { value: 11, text: 'K (64,001 - 65,000 lbs)' },
    { value: 12, text: 'L (65,001 - 66,000 lbs)' },
    { value: 13, text: 'M (66,001 - 67,000 lbs)' },
    { value: 14, text: 'N (67,001 - 68,000 lbs)' },
    { value: 15, text: 'O (68,001 - 69,000 lbs)' },
    { value: 16, text: 'P (69,001 - 70,000 lbs)' },
    { value: 17, text: 'Q (70,001 - 71,000 lbs)' },
    { value: 18, text: 'R (71,001 - 72,000 lbs)' },
    { value: 19, text: 'S (72,001 - 73,000 lbs)' },
    { value: 20, text: 'T (73,001 - 74,000 lbs)' },
    { value: 21, text: 'U (74,001 - 75,000 lbs)' },
    { value: 22, text: 'V (Over 75,000 lbs' },
  ];

  // form validation rules
  const validationSchema = Yup.object().shape({
    FirstUsedMonth: Yup.string().required('Please choose the First Used Month'),
    WeightCategoryId: Yup.number().moreThan(
      0,
      'Please choose the Taxable gross weight'
    ),
    TaxableVehicleCount: Yup.string().required(
      'Taxable Vehicle Count is required'
    ),
    EmailAddress: Yup.string()
      .required('Email Address is required')
      .email('Enter valid Email Address'),
  });

  const defaultTaxCalculator: TaxCalculatorType = {
    FirstUsedMonth: '',
    WeightCategoryId: 0,
    TaxableVehicleCount: 0,
    IsLogging: false,
    EmailAddress: '',
    TaxAmount: 0,
  };

  const { handleSubmit, control, setValue, getValues } =
    useForm<TaxCalculatorType>({
      mode: 'onChange',
      resolver: yupResolver(validationSchema) as unknown as Resolver<
        TaxCalculatorType,
        any
      >,
      defaultValues: defaultTaxCalculator,
    });

  const [taxAmount, setTaxAmount] = useState(0);

  const getLastDateOfNextMonth = (dateParams: Date) => {
    const lastDate = new Date(
      dateParams.getFullYear(),
      dateParams.getMonth() + 2,
      0
    );
    return lastDate;
  };

  const getDayOfWeek = (dateParams: Date) => {
    const daysOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const dayOfWeek = daysOfWeek[dateParams.getDay()];
    return dayOfWeek;
  };

  const formatDateToString = (dateParams: Date) => {
    const year = dateParams.getFullYear();
    const month = String(dateParams.getMonth() + 1).padStart(2, '0');
    const day = String(dateParams.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isFrbHoliday = (dateParams: Date) => {
    const data: FrbHolidayType[] = frbholidays;
    return data.some((a) => a.Holiday === formatDateToString(dateParams));
  };

  const calculateDueDate = (fum: number, fuy: number) => {
    const firstUsedMonthDate = new Date(fuy, fum - 1, 1);
    const lastDateOfMonth = getLastDateOfNextMonth(firstUsedMonthDate);
    let dayString = getDayOfWeek(lastDateOfMonth);
    setDueDate(lastDateOfMonth);
    while (
      dayString === 'Saturday' ||
      dayString === 'Sunday' ||
      isFrbHoliday(lastDateOfMonth)
    ) {
      lastDateOfMonth.setDate(lastDateOfMonth.getDate() + 1);
      dayString = getDayOfWeek(lastDateOfMonth);
      setDueDate(lastDateOfMonth);
    }
  };

  async function calculateTax(token: any, formdata: TaxCalculatorType) {
    const data: Form2290Taxes[] = form2290taxes;

    const firstUsedMonth = getValues('FirstUsedMonth')?.toString().split('-');
    const firstUsedMonthItem0 = Number(firstUsedMonth[0] ?? 0);
    const firstUsedMonthItem1 = Number(firstUsedMonth[1] ?? 0);
    const weightCategoryId = Number(getValues('WeightCategoryId'));
    const taxableVehicleCount = Number(getValues('TaxableVehicleCount'));

    const taxamount = data.filter(
      (a) =>
        a.FirstUsedMonth === firstUsedMonthItem0 &&
        a.FirstUsedYear === firstUsedMonthItem1 &&
        a.TaxYear === taxYear &&
        a.WeightCategoryId === weightCategoryId
    )[0];
    let calculatedTaxAmount = 0;
    if (isLogging) {
      calculatedTaxAmount =
        (taxamount?.LoggingTaxAmount ?? 0) * taxableVehicleCount;
    } else {
      calculatedTaxAmount = (taxamount?.TaxAmount ?? 0) * taxableVehicleCount;
    }

    setValue('TaxAmount', calculatedTaxAmount);
    setTaxAmount(calculatedTaxAmount);
    calculateDueDate(firstUsedMonthItem0, firstUsedMonthItem1);
    setShowCalculateButton(false);

    /* send data to the server */
    const postData: TaxCalculatorType = formdata;
    postData.TaxAmount = calculatedTaxAmount;
    postData.Token = token;
    const address = `/api/form2290taxcalculator/`;
    const contactusResponse = await fetch(address, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
    const contactusResponseData = await contactusResponse.json();
    if (contactusResponseData?.status === 'Success') {
      // reset();
      // setSnackOpen(true);
    }
    setIsLoading(false);
  }

  async function decrementCounter() {
    const count = Number(getValues('TaxableVehicleCount'));
    if (count > 0) setValue('TaxableVehicleCount', count - 1);
    setShowCalculateButton(true);
  }

  async function incrementCounter() {
    const count = Number(getValues('TaxableVehicleCount'));
    setValue('TaxableVehicleCount', count + 1);
    setShowCalculateButton(true);
  }

  const onChangeIsLogging = (event: any) => {
    if (event.target.checked === true) {
      setIsLogging(true);
    } else {
      setIsLogging(false);
    }
    setShowCalculateButton(true);
  };

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: TaxCalculatorType) => {
    setIsLoading(true);
    if (isShowCalculateButton) {
      setShowCalculateButton(!isShowCalculateButton);
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
            action: 'submit',
          })
          .then(async (token: any) => {
            await calculateTax(token, data);
          })
          .catch(() => {});
      });
    } else {
      router.push(
        `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup?emailaddress=${data.EmailAddress}`
      );
    }
  };

  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Tax Calculator | Calculate Heavy Vehicle Tax Online"
          description="Quickly estimate your Form 2290 tax liability using our efficient tax calculator for free. Simplify heavy vehicle tax calculations and stay compliant with ease."
          keywords="Form 2290 Tax Calculator, HVUT Calculator, Form 2290, Truck tax calculator"
          canonical="tools/form-2290-tax-calculator/"
        />
      }
    >
      <ComingSoonPopup open={open} setOpen={setOpen} />
      <Loader isLoading={isLoading} />
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                Form 2290{' '}
                <span className="highlight-container">
                  <span className="highlight">Tax Calculator</span>
                </span>{' '}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                Are you tired of complex Form 2290 tax calculations? With our
                HVUT calculator, get to know your Heavy Vehicle Use Tax (HVUT)
                easily.
              </Typography>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href="#calculator"
                  >
                    Calculate HVUT Now
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '0px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
              className="banner-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source srcSet="/assets/images/tools-img.webp" />
                <source srcSet="/assets/images/tools-img.png" />
                <img
                  src="/assets/images/tools-img.png"
                  alt="Form 2290 Tax Calculator"
                  className="img-responsive-banner"
                  width={660}
                  height={544}
                />
              </picture>
            </Box>
          </Hidden>
          {/* <Hidden lgUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
              className="banner-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/tools-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/tools-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/tools-img-sm.png"
                  alt="Form 2290 Tax Calculator"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden> */}
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',

          mx: '0 !important',
          width: '100% !important',
          py: { xs: 1, sm: 3 },
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            m={2}
            pb={3}
            className="MuiGrid-root-width"
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Why Choose Our Form 2290 Tax Calculator?
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Let go of the hassle of traditional tax filing methods and
                switch to the future of tax filing.
              </Typography> */}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/easy-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/easy-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/easy-icon.png"
                      alt="Quick and Easy"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Quick and Easy
                </Typography>
                <Typography variant="body1">
                  Calculate your HVUT amount within minutes without the need for
                  complex calculations.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/calculate-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/calculate-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/calculate-icon.png"
                      alt="Accurate"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Accurate
                </Typography>
                <Typography variant="body1">
                  The 2290 tax amount calculated is precise and accurate based
                  on the latest IRS regulations.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free
                </Typography>
                <Typography variant="body1">
                  No need to hire expensive tax professionals or purchase costly
                  software. It’s absolutely free.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      {/* Tax Calculator Section Start */}
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          py: 0,
          transition: 'all 0.3s ease 0s;',
          scrollMarginTop: 64,
        }}
        spacing={0}
        m={0}
        id="calculator"
      >
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Grid
            container
            spacing={2}
            m={0}
            mt={0}
            sx={{ flexDirection: { xs: 'column', md: 'row' } }}
            className="MuiGrid-root-width"
          >
            <Grid
              size={{ xs: 12, md: 6.5, sm: 12 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: {
                  lg: 'left',
                  md: 'left',
                  sm: 'center',
                  xs: 'center',
                },
              }}
            >
              <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Calculate your Form 2290 tax in less than a minute
                </Typography>

                <Typography
                  component="div"
                  variant="body1"
                  sx={{ mb: 3, color: '#6e6e6e' }}
                >
                  <ul
                    style={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                    }}
                  >
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 20,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          color: '#1da94a',
                          mr: 1,
                          alignSelf: 'flex-start',
                        }}
                      />{' '}
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          First Used Month:
                        </span>{' '}
                        Select the month in which your vehicle was first used on
                        public highways during the tax period.
                      </Typography>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 20,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          color: '#1da94a',
                          mr: 1,
                          alignSelf: 'flex-start',
                        }}
                      />{' '}
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          Vehicle Weight Category:
                        </span>{' '}
                        Choose the appropriate weight category that applies to
                        your vehicle. The weight category determines the tax
                        rate.
                      </Typography>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 20,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          color: '#1da94a',
                          mr: 1,
                          alignSelf: 'flex-start',
                        }}
                      />{' '}
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          Taxable Vehicle Count:
                        </span>{' '}
                        Enter the total number of taxable vehicles you own or
                        are responsible for during the tax period.
                      </Typography>
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          color: '#1da94a',
                          mr: 1,
                          alignSelf: 'flex-start',
                        }}
                      />{' '}
                      <Typography>
                        <span style={{ fontWeight: 600 }}>
                          Logging Vehicle Count:
                        </span>{' '}
                        Enter the number of vehicles used exclusively for
                        logging purposes during the tax period. For logging
                        vehicles, you pay 25% less HVUT payment.
                      </Typography>
                    </li>
                  </ul>
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 0.5 }} />
            <Grid
              size={{ xs: 12, md: 5 }}
              textAlign="left"
              sx={{ pt: '0 !important' }}
            >
              <Box
                component="form"
                autoComplete="off"
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  border: '2px solid #ccc',
                  px: 3,
                  py: 4,
                  borderRadius: 4,
                  boxShadow: '0 0 20px rgba(0,0,0,.12)',
                  transition: 'all 0.3s ease 0s;',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    mt: -4,
                    mb: 1,
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  Form 2290 Tax Calculator
                </Typography>
                <Stack direction="column" justifyContent="center" spacing={3}>
                  <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                    <InputLabel color="secondary">*First Used Month</InputLabel>
                    <Controller
                      name="FirstUsedMonth"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <Select
                            native
                            labelId="FirstUsedMonth"
                            name="FirstUsedMonth"
                            id="FirstUsedMonth"
                            label="*First Used Month"
                            color="secondary"
                            error={!!error}
                            className={value ? 'prefilled' : 'mandatory-select'}
                            value={value?.toString()}
                            onChange={(event) => {
                              onChange(event);
                              setShowCalculateButton(true);
                            }}
                          >
                            <option key="empty" value="" />

                            {taxPeriods.map((item, index: number) => {
                              return (
                                <option key={index} value={item.value}>
                                  {item.text}
                                </option>
                              );
                            })}
                          </Select>
                        </>
                      )}
                    />
                  </FormControl>
                  <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                    <InputLabel color="secondary">*Weight Category</InputLabel>
                    <Controller
                      name="WeightCategoryId"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <Select
                            native
                            labelId="lblWeightCategory"
                            id="WeightCategoryId"
                            name="WeightCategoryId"
                            label="*Weight Category"
                            error={!!error}
                            color="secondary"
                            value={value?.toString()}
                            onChange={(event) => {
                              onChange(event);
                              setShowCalculateButton(true);
                            }}
                            className={
                              value && value > 0
                                ? 'prefilled'
                                : 'mandatory-select'
                            }
                            sx={{
                              fontFamily: 'Poppins !important',
                            }}
                          >
                            <option aria-label="None" value={0} />
                            <optgroup
                              label="Most Common"
                              style={{
                                color: '#637381',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: 14,
                              }}
                            >
                              <option
                                key={22}
                                value={22}
                                style={{
                                  color: '#14181f',
                                  fontFamily: 'Poppins !important',
                                  fontWeight: 700,
                                  fontSize: 16,
                                }}
                              >
                                V (Over 75000 lbs)
                              </option>
                            </optgroup>
                            <optgroup
                              label="Others"
                              style={{
                                color: '#637381',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                fontSize: 14,
                              }}
                            >
                              {weightcategories?.map(
                                (weightCategory: any, index: number) => (
                                  <option
                                    key={index}
                                    value={weightCategory.value}
                                    style={{
                                      color: '#14181f',
                                      fontFamily: 'Poppins !important',
                                      fontWeight: 500,
                                      fontSize: 16,
                                    }}
                                  >
                                    {weightCategory.text}
                                  </option>
                                )
                              )}
                            </optgroup>
                          </Select>
                          {error && (
                            <FormHelperText error>
                              {error.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </FormControl>
                  <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                    <Controller
                      name="TaxableVehicleCount"
                      control={control}
                      defaultValue={0}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <TextField
                            id="TaxableVehicleCount"
                            type="number"
                            label="Taxable Vehicle Count"
                            variant="outlined"
                            color="secondary"
                            value={value}
                            sx={{
                              '& .MuiInputBase-root': {
                                '& input': {
                                  textAlign: 'center',
                                },
                              },
                            }}
                            onChange={(event) => {
                              onChange(event);
                              setShowCalculateButton(true);
                            }}
                            error={!!error}
                            inputProps={{
                              maxLength: 1,
                            }}
                            helperText={error ? error.message : null}
                            InputProps={{
                              inputProps: {
                                maxLength: 3,
                              },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    aria-label="reduce"
                                    onClick={() => decrementCounter()}
                                    edge="end"
                                  >
                                    <RemoveOutlinedIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="increase"
                                    onClick={() => incrementCounter()}
                                    edge="end"
                                  >
                                    <AddOutlinedIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </>
                      )}
                    />
                  </FormControl>
                  <FormControl
                    sx={{ m: 0, mt: '12px !important', minWidth: 150 }}
                    fullWidth
                  >
                    <Controller
                      name="IsLogging"
                      control={control}
                      defaultValue={false}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          className="bold"
                          sx={{
                            mr: 0,
                            textAlign: 'left',
                            color: '#14181F',
                            ml: 0,
                            backgroundColor: isLogging ? '#ecedf1' : '#fbfbfb',
                            borderRadius: 2,
                          }}
                          control={
                            <Checkbox
                              id="IsLogging"
                              checked={isLogging}
                              value={value}
                            />
                          }
                          onChange={(event) => {
                            onChange(event);
                            onChangeIsLogging(event);
                          }}
                          label={
                            <React.Fragment>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: { xs: 13, sm: 14, md: 16 },
                                }}
                              >
                                Is Logging?
                              </Box>
                            </React.Fragment>
                          }
                          value={value}
                        />
                      )}
                    />
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: { xs: 11, sm: 12, md: 13 },
                        borderRadius: 2,
                        mt: 0.5,
                        '& .MuiAlert-message': { py: 0 },
                        px: '8px !important',
                      }}
                    >
                      Click the checkbox if your vehicles are used only for
                      logging
                    </Alert>
                  </FormControl>
                  <FormControl sx={{ m: 0, minWidth: 150 }} fullWidth>
                    <Controller
                      name="EmailAddress"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          className={value ? 'prefilled' : 'mandatory'}
                          id="EmailAddress"
                          label="*Email Address"
                          variant="outlined"
                          autoComplete="nope"
                          color="secondary"
                          inputProps={{
                            maxLength: 100,
                          }}
                          value={value}
                          onChange={onChange}
                          error={!!error}
                          helperText={error ? error.message : null}
                        />
                      )}
                    />
                  </FormControl>
                  {!isShowCalculateButton && !isLoading ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        transition: 'all 0.3s ease 0s;',
                        backgroundColor: '#fff4e5',
                        borderRadius: 3,
                        p: 2,
                      }}
                    >
                      Your estimated tax
                      <br />
                      <Box
                        component="b"
                        sx={{
                          fontSize: { xs: 20, md: 24 },
                          color: '#213e99',
                        }}
                      >
                        {getUsDollarFormattedAmount(taxAmount)}
                      </Box>
                      <br />
                      <Box sx={{ fontWeight: 600 }}>
                        File before{' '}
                        <Box
                          component="span"
                          sx={{ color: '#1da94a', fontWeight: 700 }}
                        >
                          {dayjs(dueDate).format('MMM DD, YYYY')}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    ''
                  )}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      className="btn-primary"
                      sx={{
                        transition: 'all 0.3s ease 0s',
                        '&:hover, &:focus, &:active': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      {isShowCalculateButton
                        ? 'Calculate Tax'
                        : 'E-file Form 2290 Now'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      {/* Tax Calculator Section End */}

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
          }}
        >
          Calculating HVUT manually and filing Form 2290 is complex and costly
        </Typography>
        <Container maxWidth="md" sx={{ px: 0 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
              fontWeight: 400,
              color: '#f3f3f3',
              mb: 3,
              lineHeight: 1.5,
            }}
          >
            Let TaxZerone automatically calculate your HVUT amount and
            effortlessly complete the Form 2290 filing process for you.
          </Typography>
        </Container>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290taxcalculator`}
          >
            E-file Form 2290 Now
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: '#dfeae1' }}>
            Takes less than a minute
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is Form 2290?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Form 2290 is used to report and pay Heavy Vehicle Use Tax
                    (HVUT) to the IRS. It applies to vehicles with a gross
                    weight of 55,000 pounds or more that are used on public
                    highways.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. How is the Heavy Vehicle Use Tax calculated?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>
                      The tax amount is determined based on the vehicle weight
                      category and the number of vehicles you own or are
                      responsible for. The tax rates vary depending on the
                      weight category.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Where can I find the information needed for the HVUT
                  calculator?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    You can find the first used month on your vehicle&apos;s
                    registration or purchase documents. The vehicle weight
                    category can usually be found on the vehicle registration or
                    door sticker. The vehicle count and logging vehicle count
                    can be obtained from your records or fleet management
                    system.
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': {
                    borderWidth: 2,
                  },
                }}
                href="/faqs/form-2290/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default TaxCalculator;
